import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from './shell/AppShell';
import { NAV_ENTRIES } from './navigation';
import { hrefFor, readPath, subscribe as subscribeToPath, writePath } from './routing';
import type { NavItem } from './shell/shellState';
import { NotificationPanel } from '../modules/notifications/NotificationPanel';
import { notificationsApi } from '../lib/api/notifications';
import { EnrolmentRequiredNotice } from '../modules/auth/EnrolmentRequiredNotice';
import { ForgotPasswordScreen } from '../modules/auth/ForgotPasswordScreen';
import { LoginPage } from '../modules/auth/LoginPage';
import { MfaChallengeScreen } from '../modules/auth/MfaChallengeScreen';
import { RecoveryCodeScreen } from '../modules/auth/RecoveryCodeScreen';
import { ResetPasswordScreen } from '../modules/auth/ResetPasswordScreen';
import { useSession } from '../modules/auth/session';
import { AuditLogScreen } from '../modules/audit/AuditLogScreen';
import { CaseScreen } from '../modules/cases/CaseScreen';
import { CodeScreen } from '../modules/codes/CodeScreen';
import { DeviceScreen } from '../modules/devices/DeviceScreen';
import { EscalationScreen } from '../modules/escalations/EscalationScreen';
import { HomePage } from '../modules/home/HomePage';
import { JustificationScreen } from '../modules/justifications/JustificationScreen';
import { KnowledgeScreen } from '../modules/knowledge/KnowledgeScreen';
import { ReplacementScreen } from '../modules/replacements/ReplacementScreen';
import { RequestScreen } from '../modules/requests/RequestScreen';
import { TemplateCatalogue } from '../modules/sms/TemplateCatalogue';
import { RoleScreen } from '../modules/admin/RoleScreen';
import { UserListScreen } from '../modules/admin/UserListScreen';
import { ChangePasswordScreen } from '../modules/profile/ChangePasswordScreen';
import { SettingsScreen } from '../modules/profile/SettingsScreen';
import { ReferenceScreen } from '../modules/reference/ReferenceScreen';
import { SendSmsScreen } from '../modules/sms/SendSmsScreen';
import { SupportScreen } from '../modules/support/SupportScreen';
import { AdminSettingsScreen } from '../modules/settings/AdminSettingsScreen';
import {
  getPendingWrites,
  MFA_REQUIRED_STATE,
  type ApiUser,
  type MfaChallenge,
} from '../lib/api/client';
import { mfaApi } from '../lib/api/mfa';
import { canOpenAdminSettings } from '../lib/permissions/adminSettings';
import { canReadEscalations } from '../lib/permissions/escalations';
import { canReadJustifications } from '../lib/permissions/justifications';
import { canReadKnowledge } from '../lib/permissions/knowledge';
import { canReadReplacements } from '../lib/permissions/replacements';
import styles from './App.module.css';

/**
 * `ReportsScreen` is lazy-loaded (final whole-branch review, fix 2), not
 * imported eagerly like every other screen above. Measured from the
 * committed sourcemap: `recharts` alone contributes ~755 KB of bundled
 * source (5.6x react-dom), and installing it pulled in 36 production
 * packages including a whole Redux runtime (`@reduxjs/toolkit`,
 * `react-redux`, `redux`, `redux-thunk`, `immer`, `reselect`) that nothing
 * else in this application uses. Eagerly imported, all of that rode in the
 * single entry chunk and was downloaded at the LOGIN screen, by every user,
 * for a dashboard that lives one nav click behind `case.view` and that a
 * Call-Center agent (a role that lacks the permission) never even has a nav
 * entry for. `lazy()` defers the `import()` to the moment `rendersReports`
 * below actually turns true.
 *
 * Resolves against `ReportsScreen.tsx`'s own default export, added
 * alongside its pre-existing named export for exactly this — see that
 * module's own docblock on why a default export was the narrower change.
 */
const ReportsScreen = lazy(() => import('../modules/reports/ReportsScreen'));

/**
 * Application root, and the authentication gate.
 *
 * Three states, and the middle one is the one the plan does not mention:
 *
 * - **checking** — the session is an `HttpOnly` cookie, so the only way to find
 *   out whether one exists is to ask `GET /auth/me`. Rendering the login screen
 *   during that round trip shows a sign-in form to somebody who is already
 *   signed in on every single reload, and if they start typing they spend one
 *   of their five throttle attempts. It is a `role="status"` region rather than
 *   a spinner so the wait is announced rather than merely animated.
 * - **anonymous** — `LoginPage`, rendered as a SIBLING of the shell and never
 *   inside it (task 24 §11.2): a shell whose nav goes nowhere is a nav that
 *   lies, and every one of its links would 401.
 * - **authenticated** — the shell, with `HomePage` as its children.
 *
 * The nav still holds a single active path in state rather than a router:
 * Phase 1 ships one screen behind the gate, and inventing a routing layer to
 * serve it would be a decision made without the requirement that motivates it.
 */
export interface AppProps {
  /**
   * TEST SEAM ONLY. Production never passes this — `main.tsx` mounts
   * `<App />` bare, and `activePath` always starts at `'/'` there, exactly as
   * it did before this prop existed. It exists because `rendersAuditLog`
   * (below) guards a path with NO nav entry for a session that lacks
   * `audit.view` — unlike `/users`, which a permission-less caller can click
   * their way into because that entry renders unconditionally, there is no
   * click that reaches `/audit-log` for such a session, so the guard is
   * otherwise untestable except by starting a render already there.
   */
  initialPath?: string;
}

export default function App({ initialPath }: AppProps = {}) {
  const { t } = useTranslation();
  const { session, signIn, signOut } = useSession();
  /*
   * THE URL IS THE SOURCE OF TRUTH ON THE FIRST FRAME, and React state
   * thereafter. `readPath()` is what makes a reload land where the reader
   * was rather than on the dashboard — see `routing.ts` for why the scheme
   * is the fragment and not a real path.
   *
   * `initialPath` still wins when a test passes one: those renders never
   * touch `window.location`, so reading the (empty) hash would answer `'/'`
   * and defeat the seam that exists to reach `/audit-log` without a click.
   */
  const [activePath, setActivePath] = useState(initialPath ?? readPath());

  /**
   * Navigation, in the one direction the address bar cares about: state
   * moves BECAUSE the URL moved, never the other way round. `writePath`
   * fires `hashchange`, the subscription below hears it, and that single
   * listener is what sets state — so a nav click and the browser's Back
   * button arrive through identical code, and there is no second path that
   * could drift out of step with the URL.
   */
  const navigate = useCallback((path: string) => {
    // NO NAVIGATION WHILE A SAVE IS IN FLIGHT. `SavingGuard` already blocks
    // the clicks that would get here; this catches a screen that navigates on
    // its own initiative mid-save.
    if (getPendingWrites() > 0) return;
    writePath(path);
    // ...and set it directly too. `writePath` is a documented no-op when the
    // hash already holds this value, which happens whenever a screen asks to
    // go somewhere the URL already names (`onCancel` from `/password` on a
    // session that arrived there by typing it). No event would fire, and the
    // navigation would be silently dropped. Setting state to a value it
    // already holds is React's own no-op, so this cannot double-render.
    setActivePath(path);
  }, []);

  /*
   * The browser's Back/Forward and a typed hash arrive here rather than
   * through `navigate`, so the save lock is enforced here too: mid-save, the
   * address bar is put back (`replaceState` fires no `hashchange`, so this
   * cannot loop) and the screen stays where its save is.
   */
  const activePathRef = useRef(activePath);
  useEffect(() => {
    activePathRef.current = activePath;
  }, [activePath]);
  useEffect(
    () =>
      subscribeToPath((path) => {
        if (getPendingWrites() > 0) {
          window.history.replaceState(window.history.state, '', hrefFor(activePathRef.current));
          return;
        }
        setActivePath(path);
      }),
    [],
  );
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);
  /*
   * NO "REMEMBER ME" STATE HELD HERE (Task 4, M-18, round 1 review fix for
   * m-3). An earlier version carried the login form's checkbox value across
   * the challenge to `mfaApi.verify()` by hand. The API now derives it from
   * the challenge row itself — stamped once, at `POST /auth/login` — so
   * there is nothing for this component to hold or forward.
   */

  /**
   * The two account-recovery screens, held the same way `challenge` and
   * `usingRecoveryCode` are: plain React state, read only from the
   * anonymous branch below, never a router and never storage.
   *
   * `recoveryEmail` is read once by `ResetPasswordScreen` as its starting
   * value — see that component's own prop docblock — and is `''` when the
   * screen is reached through `onHaveCode` (decision R-8: no code has been
   * requested from this tab, so there is nothing to pre-fill).
   */
  const [recoveryScreen, setRecoveryScreen] = useState<'none' | 'forgot' | 'reset'>('none');
  const [recoveryEmail, setRecoveryEmail] = useState('');

  /**
   * The bell's badge (M-16, Task 4, spec §3) — the account-wide unread
   * total, and the one piece of this the shell keeps for itself. The panel
   * is unmounted whenever it is closed (most of the time), so the count
   * cannot live there; it lives here, next to `activePath`, and is handed
   * down as a prop the same way `activePath` is.
   *
   * Fetched on mount and corrected by `NotificationPanel`'s own
   * `onCountChange` after every mark-read — NEVER on an interval. Spec §3
   * is explicit that nothing here is time-critical enough to justify a
   * permanent polling loop, and `notificationsApi.list()`'s own envelope
   * already refreshes this figure for free every time the panel opens (see
   * `NotificationPanel.tsx`'s own docblock).
   *
   * ## The mount fetch cannot clobber a fresher count (review round 1,
   * Important 1)
   *
   * `unreadCount()` below is an INDEPENDENT request racing the panel's own
   * `list()`/`markRead()`/`markAllRead()` calls. A user who opens the bell
   * and clears it before this slower mount fetch resolves would otherwise
   * watch the correct, fresh `0` get overwritten by the stale pre-interaction
   * number the moment the late response lands — the panel's report and the
   * mount fetch's report have no ordering between them by default.
   *
   * `notificationCountGeneration` is bumped by the ONE function that ever
   * writes `notificationCount` (`reportNotificationCount`, below). The mount
   * effect records the generation it started at and, on resolving, applies
   * its own count only if nothing else has reported since — the identical
   * "is my answer still the latest one" question `ReplacementList.tsx`'s own
   * `requestSeq` ref answers for out-of-order list responses, adapted here
   * for two DIFFERENT request shapes writing one shared value rather than
   * two calls to the same one.
   */
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);
  const notificationCountGeneration = useRef(0);

  function reportNotificationCount(count: number): void {
    notificationCountGeneration.current += 1;
    setNotificationCount(count);
  }

  useEffect(() => {
    if (session.status !== 'authenticated') return;
    let cancelled = false;
    const startGeneration = notificationCountGeneration.current;

    notificationsApi
      .unreadCount()
      .then((count) => {
        if (cancelled) return;
        // Something fresher (the panel opening, a mark-read) already reported
        // since this request started — that report is newer information than
        // this one, so it must not be overwritten.
        if (notificationCountGeneration.current !== startGeneration) return;
        reportNotificationCount(count);
      })
      .catch(() => {
        // Best-effort: a failed fetch leaves the badge at its last known
        // value rather than surfacing an error over the whole shell for a
        // single number in a topbar icon.
      });

    return () => {
      cancelled = true;
    };
  }, [session.status]);
  /**
   * Set for exactly the render of `LoginPage` that follows a successful
   * reset, and read nowhere else. `ResetPasswordScreen` hands back no user
   * resource (R-2 — see its own docblock), so this boolean, not a value from
   * the API, is the whole of what this render needs to know.
   */
  const [resetNotice, setResetNotice] = useState(false);

  /**
   * A redeemed challenge is a SPENT challenge, and this is the one place that
   * says so.
   *
   * Signing in was not the whole of it: the token is consumed server-side the
   * moment it is redeemed, so leaving it in state leaves a credential that is
   * already dead. Nothing reads it while the shell is up — and that is exactly
   * why it went unnoticed until a real browser pressed "sign out". The
   * anonymous branch then found a challenge, decided the user was mid-sign-in,
   * and rendered the code screen instead of the sign-in form. Every code typed
   * into it went to a consumed token and came back "not accepted" while being
   * perfectly correct, and on the recovery variant each attempt burned a
   * one-time code off the user's paper.
   *
   * Clearing it HERE rather than in each handler is deliberate: a third way to
   * redeem a challenge (WebAuthn, in a later module) gets the reset by calling
   * this, instead of by remembering to.
   *
   * `usingRecoveryCode` IS DELIBERATELY NOT RESET HERE. The first version did
   * reset it and a mutation run showed the line was unobservable — with the
   * challenge gone the anonymous branch renders the sign-in form whatever the
   * flag says, so no test could tell the difference. The flag is set to false
   * where a challenge BEGINS (`onChallenged`, below), which is the one moment
   * its value decides anything. Two writers for one flag, one of them dead, is
   * how the two drift apart later.
   */
  function completeChallenge(user: ApiUser): void {
    setChallenge(null);
    signIn(user);
  }

  /*
   * EVERY entry is gated now. `auditLog` was once the only one, the rest
   * being pre-existing Phase 1 shell left ungated; `users` and
   * `dispensaries` were the last two still showing to accounts that cannot
   * open them. `session` may still be `checking` or
   * `anonymous` here: `navItems` is computed unconditionally (a Hook cannot
   * follow the early returns below it), and the shell that reads this value
   * only ever mounts once `session.status === 'authenticated'` — but the
   * memo itself has to survive being evaluated before that is true.
   */
  const navItems = useMemo<readonly NavItem[]>(() => {
    const permissions = session.status === 'authenticated' ? session.user.permissions : [];
    return NAV_ENTRIES.filter(
      (entry) => entry.id !== 'auditLog' || permissions.includes('audit.view'),
    )
      .filter((entry) => entry.id !== 'dispensaryCodes' || permissions.includes('code.manage'))
      .filter((entry) => entry.id !== 'cases' || permissions.includes('case.view'))
      .filter((entry) => entry.id !== 'reports' || permissions.includes('case.view'))
      .filter((entry) => entry.id !== 'requests' || permissions.includes('request.view'))
      .filter((entry) => entry.id !== 'sendSms' || permissions.includes('sms.send'))
      .filter((entry) => entry.id !== 'escalations' || canReadEscalations(permissions))
      .filter((entry) => entry.id !== 'justifications' || canReadJustifications(permissions))
      .filter((entry) => entry.id !== 'knowledgeBase' || canReadKnowledge(permissions))
      .filter((entry) => entry.id !== 'replacements' || canReadReplacements(permissions))
      .filter((entry) => entry.id !== 'smsTemplates' || permissions.includes('sms.template.manage'))
      .filter((entry) => entry.id !== 'adminSettings' || canOpenAdminSettings(permissions))
      /*
       * `users` AND `dispensaries` WERE THE TWO ENTRIES THIS CHAIN FORGOT.
       *
       * A pharmacist holds neither `user.view` nor `reference.manage`, so
       * both screens were offered in the nav and then failed at the API —
       * the worst of both, since the operator learns the page exists and
       * that they are not allowed it, which is exactly the enumeration the
       * rest of this list avoids by simply not rendering the entry.
       *
       * `dispensaries` IS NOT GATED, and a gate on it was removed after
       * being added here in error. The screen is not a management surface:
       * without `reference.manage` it still renders three readable tabs
       * (dispensaries, cities, centres) and hides only Import — the
       * hierarchy is browsable by design, which is the same reason
       * `ReferencePolicy::viewAny` asks for no permission at all.
       */
      .filter((entry) => entry.id !== 'users' || permissions.includes('user.view'))
      .filter((entry) => entry.id !== 'roles' || permissions.includes('role.manage'))
      .map((entry) => ({
        id: entry.id,
        label: t(entry.labelKey),
        path: entry.path,
        icon: entry.icon,
        group: entry.group,
      }));
  }, [t, session]);

  if (session.status === 'checking') {
    return (
      <div className={styles['boot']} data-testid="sessionCheck">
        {/*
          Mounted WITH its text rather than empty-then-filled, and that is the
          one place in this application where it is the right way round: this
          region exists for exactly one message, which is present from the first
          frame, so there is no later change for a screen reader to miss. The
          empty-region rule (task 23) is about text that arrives AFTER the page
          has settled.
        */}
        <p role="status">{t('auth.checkingSession')}</p>
      </div>
    );
  }

  if (session.status === 'anonymous') {
    /*
     * The second step of signing in, held HERE and in React state only.
     *
     * `challengeToken` is a credential with a five-minute life. Writing it to
     * `localStorage` or `sessionStorage` would let it outlive the tab it was
     * issued to and survive the reload that should have ended the attempt; a
     * cookie would send it to the server on requests that have no business
     * carrying it. Losing it on reload is the correct behaviour, not a gap.
     */
    if (challenge !== null && challenge.state === MFA_REQUIRED_STATE) {
      return usingRecoveryCode ? (
        <RecoveryCodeScreen
          challengeToken={challenge.challengeToken}
          onRedeem={async (token, code) => {
            completeChallenge(await mfaApi.redeemRecoveryCode(token, code));
          }}
          onUseAuthenticator={() => {
            setUsingRecoveryCode(false);
          }}
        />
      ) : (
        <MfaChallengeScreen
          challengeToken={challenge.challengeToken}
          onVerify={async (token, code) => {
            completeChallenge(await mfaApi.verify(token, code));
          }}
          onUseRecoveryCode={() => {
            setUsingRecoveryCode(true);
          }}
        />
      );
    }

    /*
     * `enrolment_required` is A DEAD END IN M-01a, and this screen says so
     * rather than pretending otherwise.
     *
     * The state means the account MUST hold a second factor and holds none.
     * The obvious response — render the enrolment screen — cannot work:
     * `POST /profile/mfa` sits behind `auth:sanctum`, and at this point the API
     * has issued no session. Any enrolment attempt from here would be a 401
     * dressed up as a code problem.
     *
     * The API side closed the route INTO this state that a user could take
     * themselves (`DELETE /profile/mfa` answers 409 while a factor is
     * mandatory), so it is now only reachable when an administrator sets
     * `mfa_required` on an account that has never enrolled. M-01b's admin user
     * management is where that gets an answer; until then the honest thing is
     * to name the situation and say who can fix it.
     */
    if (challenge !== null) {
      return (
        <EnrolmentRequiredNotice
          onBack={() => {
            setChallenge(null);
          }}
        />
      );
    }

    // ACCOUNT RECOVERY, SIGNED OUT. Two screens, two doors in — see the
    // `recoveryScreen`/`recoveryEmail` state docblock above and decision
    // R-8 on `ResetPasswordScreen`'s own docblock for why a SECOND door
    // (`onHaveCode` below, with no address requested from this tab) exists
    // at all.
    if (recoveryScreen === 'forgot') {
      return (
        <ForgotPasswordScreen
          onRequested={(requestedEmail) => {
            // THE ONLY TRIGGER. `ForgotPasswordScreen`'s own docblock (task
            // 6, "Round 1 review, Critical"): `onRequested` fires from the
            // confirmation's own click and NEVER as a reaction to
            // `api.requestPasswordReset` settling. Wiring the swap to
            // anything else — a `useEffect` keyed on state this component
            // held, a ref callback, anything that reacts to the request
            // completing rather than to this click — reintroduces the exact
            // same-commit unmount that finding exists to rule out, one level
            // up from where it was first found.
            setRecoveryEmail(requestedEmail);
            setRecoveryScreen('reset');
          }}
          onCancel={() => {
            setRecoveryScreen('none');
          }}
        />
      );
    }

    if (recoveryScreen === 'reset') {
      return (
        <ResetPasswordScreen
          email={recoveryEmail}
          onReset={() => {
            // R-2: `api.resetPassword` hands back no session and no user
            // resource, so there is nothing here to `signIn` with — the
            // person signs in through the ordinary form below, which is
            // exactly where this returns them. `resetNotice` is the whole
            // of what this render needs to remember (see its own docblock).
            setRecoveryScreen('none');
            setResetNotice(true);
          }}
          onCancel={() => {
            setRecoveryScreen('none');
          }}
        />
      );
    }

    return (
      <LoginPage
        onSignedIn={(user) => {
          setResetNotice(false);
          signIn(user);
        }}
        onChallenged={(next) => {
          setResetNotice(false);
          setUsingRecoveryCode(false);
          setChallenge(next);
        }}
        onForgotPassword={() => {
          setResetNotice(false);
          setRecoveryScreen('forgot');
        }}
        onHaveCode={() => {
          setResetNotice(false);
          // Decision R-8: no address requested from this tab, so nothing to
          // pre-fill — the person types the one they received the mail at.
          setRecoveryEmail('');
          setRecoveryScreen('reset');
        }}
        notice={resetNotice ? t('auth.passwordWasReset') : undefined}
      />
    );
  }

  // FORCED PASSWORD CHANGE. THE MIDDLEWARE IS THE CONTROL, NOT THIS CHECK.
  // `MustChangePassword` on the API side (decision S-1) answers 403 on
  // EVERY endpoint outside a three-route allowlist — `PUT /profile/password`,
  // `GET /auth/me`, `POST /auth/logout` — for as long as `must_change_password`
  // is true, regardless of what this component renders. READS ARE REFUSED
  // TOO, not only writes: the account holds a password its owner did not
  // choose, quite possibly one an administrator read aloud over the phone,
  // and letting it read case or patient data is exactly what S-1 exists to
  // prevent. Deleting this branch would not reopen a single one of those
  // routes; it would only bring back the wall of 403s this branch exists to
  // avoid showing someone who has just signed in with a temporary password.
  // No navigation renders alongside it, on purpose (task 9's own
  // requirement) — every nav entry points at a screen the API will refuse
  // until the change lands.
  //
  // `onChanged={signIn}` is `SettingsScreen`'s own precedent (task 8):
  // `api.changePassword` resolves to the updated user with
  // `must_change_password: false`, and `session.user` does not refresh
  // itself. Without this, the flag the check above reads never changes, and
  // this exact screen renders again on the very next frame — a forced flow
  // with no way out.
  if (session.user.must_change_password) {
    return <ChangePasswordScreen onChanged={signIn} />;
  }

  // THE VOLUNTARY PATH. The plan's File Structure table assigns
  // `ChangePasswordScreen.tsx` the responsibility "Voluntary and forced", and
  // this task is the only one that owns the file — task 10 wires only the
  // session list into `SettingsScreen`, and task 11 is browser verification
  // with no code. Reached from a control in `SettingsScreen` (below) rather
  // than from `NAV_ENTRIES`: a full nav entry would put "Change password"
  // permanently in the sidebar for an action taken rarely, where Settings —
  // already the "about my own account" screen — is where a person looking
  // for it would go first. It renders standalone, exactly as the forced case
  // above does (same component, same props, same "no shell while this is up"
  // choice). Landing back on `/profile` on success — not `/` — is deliberate:
  // that is where the control that started this lives.
  //
  // `onCancel` IS PASSED HERE AND NOWHERE ELSE. Round-2 review corrected a
  // wrong claim in this comment's earlier version — "leaving without
  // completing means navigating back to `/profile` the same way it was
  // reached" is FALSE. `activePath` is plain `useState('/')` (this
  // component's own docblock: "a single active path in state rather than a
  // router"), `AppShell.tsx`'s nav handler calls `preventDefault` explicitly
  // to keep every in-app path out of the URL, and nothing anywhere in
  // `src/app/` calls `pushState`. No history entry is ever pushed, so
  // Browser Back leaves the SPA entirely, and a full reload reinitialises
  // `activePath` to `'/'` — the dashboard, not Settings. Without `onCancel`,
  // a voluntary visitor who changes their mind has NO in-app way back at
  // all. The forced render two branches above deliberately gets none of
  // this — having no escape is the point there (decision S-1).
  if (activePath === '/password') {
    return (
      <ChangePasswordScreen
        onChanged={(user) => {
          signIn(user);
          navigate('/profile');
        }}
        onCancel={() => {
          navigate('/profile');
        }}
      />
    );
  }

  // `user.view` is the one thing checked here — not a general permission-
  // gated nav, which `navigation.ts` deliberately does not have yet (nine
  // entries, most pointing nowhere, all ungated: a Phase 1 shell this task
  // did not touch). Without this, every signed-in account reaches a list
  // that 403s and a Try again button that can never succeed — the exact
  // failure this screen exists to prevent, offered by the nav entry itself.
  const rendersUserList = activePath === '/users' && session.user.permissions.includes('user.view');

  // DEFENCE IN DEPTH FOR A ROUTER THIS APP DOES NOT HAVE YET, NOT A FIX FOR A
  // REACHABLE HOLE TODAY. Nothing currently reaches `/audit-log` for a
  // session without `audit.view`: `activePath` always initialises to `'/'`
  // (`useState(initialPath)` above, and `initialPath` defaults to `'/'` in
  // every real mount — see `AppProps`'s own docblock), nothing in this file
  // reads `window.location`, and — unlike `/users`, which every session can
  // click regardless of permission (`navigation.ts`'s nine original entries
  // are ungated) — the audit nav entry itself is filtered out of `navItems`
  // for exactly this session, so there is no click that lands here either.
  // The permission check stays anyway, because a URL-driven router is the
  // obvious next step for this shell and the day one arrives is the day
  // `activePath` stops being something only this component's own clicks can
  // set — this line is what keeps that future change from silently becoming
  // an authorization bug instead of a routing one.
  const rendersAuditLog =
    activePath === '/audit-log' && session.user.permissions.includes('audit.view');

  // Self-service, deliberately ungated: every signed-in account holds
  // `updateOwnProfile` over its own row (task 1 of this module's SDD), so
  // — unlike `/users` above — there is no permission this path could 403
  // against and therefore no fallback branch it needs.
  const rendersSettings = activePath === '/profile';

  // `/support` — M-15d. UNGATED, unlike every other screen flag here: the
  // endpoint behind it carries no permission either, because every
  // signed-in staff member may contact support.
  const rendersSupport = activePath === '/support';

  // `/dispensaries` — M-03's reference hierarchy (dispensary -> city ->
  // centre) and its CSV import. Deliberately ungated, the same shape as
  // `rendersSettings` immediately above: every `GET` under `/reference/*`
  // needs only `auth:sanctum`/`active` (`routes/api.php`'s own docblock,
  // "any active, authenticated account"), so there is no permission this
  // path could 403 against either. `ReferenceScreen` itself is what gates
  // every WRITE control on `reference.manage`. This is a PRE-EXISTING nav
  // entry (`navigation.ts`: one of the nine Phase 1 items, ungated, that
  // pointed nowhere until now) rather than a new one, so `navItems` needed
  // no change to surface it.
  const rendersReference = activePath === '/dispensaries';

  // `/devices` — M-04 task 10's device stock registry (sensors, readers).
  // Deliberately ungated, the identical shape `rendersReference` above
  // already established: every `GET /devices/*` route needs only
  // `auth:sanctum`/`active` (`devices.ts`'s own docblock), so there is no
  // permission this path could 403 against either. `DeviceScreen` itself is
  // what gates every create/edit/status control on `device.manage`.
  const rendersDevices = activePath === '/devices';

  // `/dispensary-codes` — M-05 task 7's dispensary code pool. GATED, the
  // `rendersAuditLog` shape rather than `rendersDevices`'s: `codes.ts`'s own
  // docblock is explicit that every one of the five `/dispensary-codes*`
  // endpoints requires `code.manage`, including the two reads, so — unlike
  // `/devices` immediately above — there IS a permission this path could
  // 403 against. `navigation.ts`'s own `dispensaryCodes` entry is filtered
  // out of `navItems` for exactly this session (see that filter above), so
  // a caller without the permission never sees a link that reaches here —
  // this check is defence in depth for the same not-yet-a-router reason
  // `rendersAuditLog`'s own comment gives.
  const rendersCodes =
    activePath === '/dispensary-codes' && session.user.permissions.includes('code.manage');

  // `/cases` — M-06 task 5's case register. GATED, the `rendersCodes` shape
  // rather than `rendersDevices`'s: UNLIKE `/devices`, `CasePolicy::view()`
  // (verified against the API repository) requires `case.view` for BOTH
  // `GET /cases` and `GET /cases/{case}` — there IS a permission this path
  // could 403 against. `navigation.ts`'s pre-existing `cases` entry (one of
  // the nine Phase 1 items that pointed nowhere) is filtered out of
  // `navItems` for exactly this session (see that filter above), so a
  // caller without the permission never sees a link that reaches here —
  // this check is defence in depth for the same not-yet-a-router reason
  // `rendersAuditLog`'s own comment gives.
  /*
   * `#/cases/{uuid}` IS THE SAME SCREEN, with one case already open. A
   * prefix match rather than a second route: the case detail is not a
   * screen of its own — it renders inside `CaseScreen`, over the register —
   * so routing it separately would mean two places that know how a case is
   * displayed.
   *
   * It also makes a case linkable, which is what the dashboard's recent
   * list needed: before this, clicking a row there did nothing at all,
   * because the only way into a case detail was to be standing in the
   * register already.
   */
  const openCaseUuid = activePath.startsWith('/cases/') ? activePath.slice('/cases/'.length) : undefined;
  const rendersRoles = activePath === '/roles' && session.user.permissions.includes('role.manage');
  const rendersCases =
    (activePath === '/cases' || openCaseUuid !== undefined) &&
    session.user.permissions.includes('case.view');

  // `/reports` — M-15b's case dashboard. GATED, the `rendersCases` shape:
  // `CasePolicy::viewAny()` requires `case.view` for `GET /cases/summary`,
  // so there IS a permission this path could 403 against. `navigation.ts`'s
  // pre-existing `reports` entry (one of the nine Phase 1 items that
  // pointed nowhere) is filtered out of `navItems` for exactly this
  // session (see that filter below), so a caller without the permission
  // never sees a link that reaches here — this check is defence in depth.
  const rendersReports =
    activePath === '/reports' && session.user.permissions.includes('case.view');

  // `/requests` — M-09 task 6's follow-up queue. GATED, the `rendersCases`
  // shape exactly: `CaseRequestPolicy::viewAny()` (verified against the API
  // repository) requires `request.view` for `GET /case-requests`, so —
  // unlike `/devices` — there IS a permission this path could 403 against.
  // `navigation.ts`'s pre-existing `requests` entry (one of the nine
  // Phase 1 items that pointed nowhere) is filtered out of `navItems` for
  // exactly this session (see that filter above), so a caller without the
  // permission never sees a link that reaches here — this check is
  // defence in depth for the same not-yet-a-router reason
  // `rendersAuditLog`'s own comment gives.
  const rendersRequests =
    activePath === '/requests' && session.user.permissions.includes('request.view');

  // `/send-sms` — the standalone send screen, the same shape `rendersRequests`
  // is: `CaseMessagePolicy::create()` (verified against the API repository)
  // requires `sms.send` for `POST /cases/{case}/messages`, and this screen's
  // entire reason to exist is that one action, so there is no lesser reading
  // this path could have without the permission at all. `navigation.ts`'s
  // pre-existing `sendSms` entry (one of the nine Phase 1 items that pointed
  // nowhere) is filtered out of `navItems` for exactly this session (see that
  // filter above), so a caller without the permission never sees a link that
  // reaches here — this check is defence in depth for the same not-yet-a-router
  // reason `rendersAuditLog`'s own comment gives.
  const rendersSendSms =
    activePath === '/send-sms' && session.user.permissions.includes('sms.send');

  // `/escalations` — M-10 Task 5's complaint escalation chain. GATED, the
  // `rendersRequests` shape exactly: `EscalationPolicy` (verified against
  // the API repository) requires `escalation.view` for both `GET
  // /escalations` and `GET /escalations/{escalation}`. `navigation.ts`'s own
  // `escalations` entry (the fourteenth, and the first that did not exist
  // before this milestone rather than one that merely pointed nowhere — see
  // that table's own docblock) is filtered out of `navItems` for exactly
  // this session (see that filter above), so a caller without the
  // permission never sees a link that reaches here — this check is defence
  // in depth for the same not-yet-a-router reason `rendersAuditLog`'s own
  // comment gives.
  const rendersEscalations =
    activePath === '/escalations' && canReadEscalations(session.user.permissions);

  // `/justifications` — M-11 Task 4's staff conduct-complaint workflow.
  // GATED, the `rendersEscalations` shape exactly: `JustificationPolicy`
  // (verified against the API repository) requires one of
  // `justification.view`/`create`/`answer`/`decide` for both `GET
  // /justifications` and `GET /justifications/{justification}` — see
  // `src/lib/permissions/justifications.ts`'s own `canReadJustifications`
  // docblock for why four permissions, not one. `navigation.ts`'s own
  // `justifications` entry already existed (one of the nine Phase 1 items
  // that pointed nowhere, unlike `escalations`, which did not exist at all)
  // and is filtered out of `navItems` for exactly this session (see that
  // filter above), so a caller without any of the four permissions never
  // sees a link that reaches here — this check is defence in depth for the
  // same not-yet-a-router reason `rendersAuditLog`'s own comment gives.
  const rendersJustifications =
    activePath === '/justifications' && canReadJustifications(session.user.permissions);

  // `/knowledge-base` — M-12 Task 4's call-centre FAQ. GATED, the
  // `rendersJustifications` shape exactly: `KnowledgeBaseArticlePolicy::
  // viewAny()` (verified against the API repository) requires
  // `knowledge.view` for both `GET /knowledge-base` and `GET
  // /knowledge-base/{article}` — see `src/lib/permissions/knowledge.ts`'s
  // own `canReadKnowledge` docblock for why one permission, not four, is
  // enough here (unlike `canReadJustifications`/`canReadEscalations`).
  // `navigation.ts`'s own `knowledgeBase` entry already existed (one of the
  // nine Phase 1 items that pointed nowhere) and is filtered out of
  // `navItems` for exactly this session (see that filter above), so a
  // caller without `knowledge.view` never sees a link that reaches here —
  // this check is defence in depth for the same not-yet-a-router reason
  // `rendersAuditLog`'s own comment gives.
  const rendersKnowledgeBase =
    activePath === '/knowledge-base' && canReadKnowledge(session.user.permissions);

  // `/replacements` — M-13 Task 5's device replacement workflow. GATED, the
  // `rendersEscalations` shape exactly: `ReplacementPolicy` (verified
  // against the API repository) admits `replacement.view` OR
  // `replacement.fulfil` for `GET /replacements` and
  // `GET /replacements/{replacement}` — see
  // `src/lib/permissions/replacements.ts`'s own `canReadReplacements`
  // docblock for why TWO permissions, not one: a fulfil-holder must never
  // get 403 reading the row it is about to act on, the M-10 lesson
  // `canReadEscalations` was written for. `navigation.ts`'s own
  // `replacements` entry is NEW in this milestone (the legacy's own two
  // links are commented out and no sidebar here had one) and is filtered
  // out of `navItems` for exactly this session (see that filter above), so
  // a caller without either permission never sees a link that reaches here
  // — this check is defence in depth for the same not-yet-a-router reason
  // `rendersAuditLog`'s own comment gives.
  const rendersReplacements =
    activePath === '/replacements' && canReadReplacements(session.user.permissions);

  // `/sms-templates` — M-18 Task 6's template management screen. GATED, the
  // `rendersCodes` shape exactly: ALL SIX endpoints behind this screen
  // (`SmsTemplateCatalogueController`/`SmsTemplateWriteController`/
  // `SmsTemplateVersionController`, verified against the API repository)
  // require `sms.template.manage`, including the catalogue read itself —
  // there IS a permission this path could 403 against. `navigation.ts`'s
  // own `smsTemplates` entry is NEW in this milestone and is filtered out
  // of `navItems` for exactly this session (see that filter above), so a
  // caller without the permission never sees a link that reaches here —
  // this check is defence in depth for the same not-yet-a-router reason
  // `rendersAuditLog`'s own comment gives.
  const rendersTemplates =
    activePath === '/sms-templates' && session.user.permissions.includes('sms.template.manage');

  // `/admin-settings` — system configuration (today: escalation routing,
  // moved here from under the escalations queue). GATED on holding the
  // permission behind at least one section, the same predicate that filters
  // its nav entry above.
  const rendersAdminSettings =
    activePath === '/admin-settings' && canOpenAdminSettings(session.user.permissions);

  // THE TITLE MUST MATCH WHAT ACTUALLY RENDERS BELOW, NOT THE CLICKED NAV
  // ENTRY. A caller without `user.view` still lands on `HomePage` (the
  // `rendersUserList` branch below), but `navItems.find` on the raw
  // `activePath` would still resolve to the "Users" entry — an `<h1>Users</h1>`
  // over the dashboard's own content, found in the same real-browser
  // verification pass that found the reset-MFA gate. Falling back to `/`
  // (the dashboard entry) keeps the shell's one `<h1>` honest about which
  // screen is actually mounted.
  //
  // `/audit-log` DELIBERATELY CARRIES NO ANALOGOUS CLAUSE HERE, and that was
  // reviewed rather than overlooked: a first version of this line added
  // `|| (activePath === '/audit-log' && !rendersAuditLog)`, and mutation
  // testing proved it dead. `navItems` (above) filters the `auditLog` entry
  // on the exact same `permissions.includes('audit.view')` check
  // `rendersAuditLog` uses — so whenever that clause would matter,
  // `navItems.find(item => item.path === '/audit-log')` has ALREADY failed,
  // and `?? navItems[0]` (below) falls back to the identical Dashboard
  // object either an explicit clause or its absence would produce. `/users`
  // has no such filter (`navigation.ts`: the nine original entries are all
  // ungated), which is the one fact that makes its own clause load-bearing
  // and this one a spare of a path nothing can reach. Removed per this
  // codebase's own rule: "two paths that cannot be told apart are one path
  // with a spare" (task 23 §6.5.5, `client.ts`).
  // `#/cases/{uuid}` is the Cases screen with one case open, so it is
  // titled (and its sidebar entry marked) as Cases, not the Dashboard fallback.
  const titlePath =
    activePath === '/users' && !rendersUserList
      ? '/'
      : openCaseUuid !== undefined
        ? '/cases'
        : activePath;
  const active = navItems.find((item) => item.path === titlePath) ?? navItems[0];

  /**
   * What the SIDEBAR shows, which is `navItems` minus `settings`.
   *
   * `/profile` is reached from the account menu in the topbar
   * (`Topbar.tsx`), so a sidebar entry for it offers the same destination
   * twice. It is dropped HERE and not from `navItems` above, because
   * `navItems` is also what resolves the shell's `<h1>` and breadcrumb — an
   * entry hidden from the sidebar still names its own screen, and filtering
   * it earlier retitled `/profile` as "Dashboard", the fallback on the line
   * above. That fallback is load-bearing for a different case (a
   * permission-gated path whose screen did not mount) and must keep working.
   */
  const sidebarItems = navItems
    .filter((item) => item.id !== 'settings')
    /*
     * DEVICES IS HIDDEN, NOT REMOVED. The stock registry has no operational
     * use in this deployment — nobody is keeping sensor and reader inventory
     * in it — so a permanent nav row for it costs every reader a line of
     * scanning on every screen. The ENTRY stays in `NAV_ENTRIES` and
     * `/devices` still renders for anyone who types it, because deleting a
     * working screen to tidy a list is a decision about the feature, and
     * this is a decision about the sidebar. See `navigation.ts` on why
     * commenting a link out is the absence of a decision rather than one.
     */
    .filter((item) => item.id !== 'devices');

  return (
    <AppShell
      navItems={sidebarItems}
      activePath={openCaseUuid !== undefined ? '/cases' : activePath}
      title={active?.label ?? t('app.name')}
      titleIcon={active?.icon}
      breadcrumb={[{ label: t('app.name'), path: '/' }, { label: active?.label ?? '' }]}
      notificationCount={notificationCount}
      onNavigate={navigate}
      onNotificationsClick={() => {
        setNotificationsPanelOpen(true);
      }}
      onSignOut={() => {
        void signOut();
      }}
    >
      {rendersCases ? (
        <CaseScreen
          /* `key` so navigating from one case link straight to another
             remounts the screen: `initialCaseUuid` seeds state on the first
             render only, and without this the second link would leave the
             first case open. */
          key={openCaseUuid ?? 'register'}
          permissions={session.user.permissions}
          initialCaseUuid={openCaseUuid}
        />
      ) : rendersReports ? (
        /*
         * No `Suspense` precedent exists anywhere else in this repository
         * (checked: no other module renders one) — this fallback is
         * therefore kept deliberately minimal: a `role="status"` region,
         * the same accessible-loading-announcement shape this component's
         * own `session.status === 'checking'` branch above already uses,
         * rather than inventing a bespoke spinner component for one screen.
         */
        <Suspense fallback={<p role="status">{t('list.loading')}</p>}>
          <ReportsScreen />
        </Suspense>
      ) : rendersRequests ? (
        <RequestScreen permissions={session.user.permissions} />
      ) : rendersSendSms ? (
        <SendSmsScreen />
      ) : rendersEscalations ? (
        <EscalationScreen permissions={session.user.permissions} />
      ) : rendersJustifications ? (
        <JustificationScreen
          permissions={session.user.permissions}
          currentUserUuid={session.user.uuid}
        />
      ) : rendersKnowledgeBase ? (
        <KnowledgeScreen permissions={session.user.permissions} />
      ) : rendersReplacements ? (
        <ReplacementScreen permissions={session.user.permissions} />
      ) : rendersTemplates ? (
        <TemplateCatalogue />
      ) : rendersAdminSettings ? (
        <AdminSettingsScreen permissions={session.user.permissions} />
      ) : rendersRoles ? (
        <RoleScreen permissions={session.user.permissions} />
      ) : rendersUserList ? (
        <UserListScreen
          permissions={session.user.permissions}
          currentUserUuid={session.user.uuid}
        />
      ) : rendersAuditLog ? (
        <AuditLogScreen />
      ) : rendersReference ? (
        <ReferenceScreen permissions={session.user.permissions} />
      ) : rendersDevices ? (
        <DeviceScreen permissions={session.user.permissions} />
      ) : rendersCodes ? (
        <CodeScreen />
      ) : rendersSettings ? (
        <SettingsScreen
          user={session.user}
          onSaved={signIn}
          onChangePassword={() => {
            navigate('/password');
          }}
        />
      ) : rendersSupport ? (
        <SupportScreen />
      ) : (
        <HomePage user={session.user} />
      )}

      <NotificationPanel
        open={notificationsPanelOpen}
        permissions={session.user.permissions}
        onClose={() => {
          setNotificationsPanelOpen(false);
        }}
        onNavigate={navigate}
        onCountChange={reportNotificationCount}
      />
    </AppShell>
  );
}
